// src/components/WelcomePage.tsx
import React, { useState } from 'react';
import './WelcomePage.css';

interface WelcomePageProps {
  onStartShift: (shiftEndTime?: Date) => void;
  userName?: string;
  isAdmin?: boolean;
  onViewAnalytics?: () => void;
}

const WelcomePage: React.FC<WelcomePageProps> = ({ 
  onStartShift, 
  userName = "Sarah Johnson",
  isAdmin = false,
  onViewAnalytics
}) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [shiftEndHour, setShiftEndHour] = useState<string>('7');
  const [shiftEndMinute, setShiftEndMinute] = useState<string>('00');
  const [shiftEndPeriod, setShiftEndPeriod] = useState<'AM' | 'PM'>('PM');

  // Calculate shift end time as a Date object
  const getShiftEndTime = (): Date => {
    let hour = parseInt(shiftEndHour, 10);
    const minute = parseInt(shiftEndMinute, 10);
    
    // Convert to 24-hour format
    if (shiftEndPeriod === 'PM' && hour !== 12) {
      hour += 12;
    } else if (shiftEndPeriod === 'AM' && hour === 12) {
      hour = 0;
    }
    
    const endTime = new Date();
    endTime.setHours(hour, minute, 0, 0);
    
    // If the end time is before now, assume it's tomorrow
    if (endTime <= new Date()) {
      endTime.setDate(endTime.getDate() + 1);
    }
    
    return endTime;
  };

  const handleButtonClick = () => {
    setFadeOut(true);
    // Call the appropriate callback after fade-out animation completes
    setTimeout(() => {
      if (isAdmin && onViewAnalytics) {
        onViewAnalytics();
      } else {
        const shiftEndTime = getShiftEndTime();
        onStartShift(shiftEndTime);
      }
    }, 800); // 800ms for fade-out animation
  };

  // Check if the form is valid (for non-admin users)
  const isFormValid = isAdmin || (shiftEndHour && shiftEndMinute);

  return (
    <div className={`welcome-page ${fadeOut ? 'fade-out' : ''}`}>
      <div className="welcome-background-dots" />
      
      <div className="welcome-content">
        <div className="welcome-logo-section">
          <div className="welcome-brand-row">
            <img src="/logo.png" alt="CareShift Logo" className="welcome-logo-image" />
            <h2 className="welcome-brand-title">CareShift</h2>
          </div>
        </div>

        <div className="welcome-text-section">
          <h1 className="welcome-title">Welcome, {userName}</h1>
        </div>

        {/* Shift End Time Input - Only for non-admin users */}
        {!isAdmin && (
          <div className="shift-end-time-section">
            <label className="shift-end-label">When does your shift end?</label>
            <div className="shift-end-inputs">
              <select 
                className="shift-end-select"
                value={shiftEndHour}
                onChange={(e) => setShiftEndHour(e.target.value)}
              >
                {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="time-separator">:</span>
              <select 
                className="shift-end-select"
                value={shiftEndMinute}
                onChange={(e) => setShiftEndMinute(e.target.value)}
              >
                {['00', '15', '30', '45'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <div className="period-toggle">
                <button 
                  className={`period-btn ${shiftEndPeriod === 'AM' ? 'active' : ''}`}
                  onClick={() => setShiftEndPeriod('AM')}
                  type="button"
                >
                  AM
                </button>
                <button 
                  className={`period-btn ${shiftEndPeriod === 'PM' ? 'active' : ''}`}
                  onClick={() => setShiftEndPeriod('PM')}
                  type="button"
                >
                  PM
                </button>
              </div>
            </div>
          </div>
        )}

        <button 
          className="start-shift-button"
          onClick={handleButtonClick}
          disabled={!isFormValid}
        >
          <span className="button-text">{isAdmin ? 'View Analytics' : 'Start Your Shift'}</span>
          <svg className="button-arrow" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default WelcomePage;
